/**
 * @fileoverview React Query hooks for admin SMS rate limit management.
 * Provides data fetching and mutations for managing tenant SMS limits.
 *
 * ## About SMS Rate Limits
 *
 * These are APPLICATION-LEVEL limits (not Twilio requirements) designed to:
 * - Prevent abuse/spam from tenants
 * - Control SMS costs
 * - Protect sender reputation (carrier filtering affects unregistered numbers)
 * - Ensure fair resource allocation across tenants
 *
 * ## Suggested Presets
 *
 * | Preset       | Hourly | Daily | Use Case                           |
 * |--------------|--------|-------|-------------------------------------|
 * | Conservative | 10     | 50    | New tenants, low trust              |
 * | Standard     | 50     | 200   | Typical business usage (default)    |
 * | High Volume  | 100    | 500   | Established tenants with 10DLC      |
 * | Very High    | 500    | 2000  | Enterprise with verified campaigns  |
 *
 * ## Twilio Context
 *
 * Twilio's limits depend on number type, 10DLC registration, and account trust.
 * Long codes: ~1 SMS/sec, Toll-free: ~3 SMS/sec, Short codes: 100+ SMS/sec.
 * Our limits should stay below Twilio's to avoid 429 errors.
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/hooks/useAuth';

export interface TenantSMSLimits {
  out_tenant_id: string;
  out_tenant_name: string;
  out_sms_rate_limit_per_hour: number;
  out_sms_rate_limit_per_day: number;
  out_current_hour_usage: number;
  out_current_day_usage: number;
}

/**
 * Fetches all tenants with their SMS rate limits and current usage.
 * Only available to platform admins.
 */
export function useAdminTenantSMSLimits() {
  const { isAdmin, loading } = useAuth();

  return useQuery({
    queryKey: ['admin-tenant-sms-limits'],
    queryFn: async (): Promise<TenantSMSLimits[]> => {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const { data, error } = await (supabase.rpc as any)(
        'calie_admin_list_tenant_sms_limits'
      );

      if (error) {
        console.error('[useAdminTenantSMSLimits] Error:', error.message);
        throw error;
      }

      return (data || []) as unknown as TenantSMSLimits[];
    },
    enabled: !loading && isAdmin?.() === true,
  });
}

/**
 * Mutation to update a single tenant's SMS rate limits.
 */
export function useAdminUpdateSMSLimits() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      tenantId,
      hourlyLimit,
      dailyLimit,
    }: {
      tenantId: string;
      hourlyLimit: number;
      dailyLimit: number;
    }) => {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const { data, error } = await (supabase.rpc as any)(
        'calie_admin_update_sms_limits',
        {
          _tenant_id: tenantId,
          _hourly_limit: hourlyLimit,
          _daily_limit: dailyLimit,
        }
      );

      if (error) {
        console.error('[useAdminUpdateSMSLimits] Error:', error.message);
        throw error;
      }

      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin-tenant-sms-limits'] });
    },
  });
}

/**
 * Mutation to bulk update SMS rate limits for multiple tenants.
 */
export function useAdminBulkUpdateSMSLimits() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      tenantIds,
      hourlyLimit,
      dailyLimit,
    }: {
      tenantIds: string[];
      hourlyLimit: number;
      dailyLimit: number;
    }) => {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const { data, error } = await (supabase.rpc as any)(
        'calie_admin_bulk_update_sms_limits',
        {
          _tenant_ids: tenantIds,
          _hourly_limit: hourlyLimit,
          _daily_limit: dailyLimit,
        }
      );

      if (error) {
        console.error('[useAdminBulkUpdateSMSLimits] Error:', error.message);
        throw error;
      }

      return data as number;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin-tenant-sms-limits'] });
    },
  });
}

/**
 * Mutation to clear rate limit counters for a tenant.
 */
export function useAdminClearRateLimits() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (tenantId: string) => {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const { data, error } = await (supabase.rpc as any)(
        'calie_admin_clear_rate_limits',
        {
          _tenant_id: tenantId,
        }
      );

      if (error) {
        console.error('[useAdminClearRateLimits] Error:', error.message);
        throw error;
      }

      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin-tenant-sms-limits'] });
    },
  });
}
